import { ref as dbRef, push, query, orderByChild, startAt, endAt, equalTo, get, onValue } from 'firebase/database';
import { db, auth } from '../firebase';
import { PLACEHOLDER_ADMIN_ACTOR } from '../constants/bookingConst';
import { phTodayStr } from './phTime';

// ---------------------------------------------------------------------------
// /auditLog — append-only history of who did what to a booking or Open Play
// record, and when.
//
// SHAPE, AND WHY IT ISN'T JUST "status old/new":
// A booking status transition (confirm/decline/cancel) genuinely has an old
// and new status. Guest booking submission doesn't — it's a creation event,
// not a transition. Open Play session creation/edit and a player joining a
// session don't touch a booking status field at all. Forcing all of these
// into one status-old/status-new shape either leaves those fields blank in a
// way that's ambiguous ("was there really no prior status, or did we just not
// record it?"), or invents a fake status just to fill the columns.
//
// So every entry carries an ACTION TYPE (what happened) and an ENTITY TYPE +
// ID (what it happened to) as the primary fields. statusOld/statusNew stay as
// optional detail, populated only when the action actually is a status
// change (see AUDIT_ACTION_TYPES below for which ones are).
//
// APPEND-ONLY: every write here is push() — a brand new key, never a set()
// or update() against an existing one. database.rules.json backs this with
// ".write": "!data.exists() && newData.exists()" on /auditLog/$entryId, so a
// script or a stray update() call in future code literally cannot edit or
// delete a past entry, only add new ones. If a booking or session needs
// correcting, the fix is a new entry describing the correction, not editing
// history.
//
// PERFORMED BY: every admin currently signs in through one shared login (see
// database.rules.json's header comment and authServiceUtil.js) — there's no
// per-admin account yet. `performedBy` below stores whatever's available
// today (the shared admin email, or the literal string 'admin' when no
// email is in scope) so the FIELD is in place and every past entry stays
// meaningful once real /admins/$uid accounts exist and start passing a real
// identity through the same parameter — nothing about this shape needs to
// change then, only what gets passed in.
// ---------------------------------------------------------------------------

// Enums, labels and defaults now live with every other constant in
// constants/bookingConst.js — see the AUDIT TRAIL section there. They are
// re-exported here so `import { AUDIT_ACTION_TYPES } from './auditLogUtil'`
// keeps working; this file remains the single home for the audit log's
// BEHAVIOUR (writing entries, reading them back, building summaries).
export {
  AUDIT_ACTION_TYPES,
  ENTITY_TYPES,
  AUDIT_ACTION_LABELS,
  STATUS_CHANGE_ACTIONS,
  PLACEHOLDER_ADMIN_ACTOR,
  GUEST_ACTOR,
  GRID_STATUS_ACTIONS,
  DEFAULT_AUDIT_DAYS,
} from '../constants/bookingConst';

/**
 * Who to record for an ADMIN action: the email of the account actually
 * signed in at the moment of the action, falling back to the generic label
 * only if Firebase has no current user.
 *
 * Read at call time rather than passed down from a component, so every call
 * site records the same thing without having to thread the session email
 * through. Note the standing caveat from this file's header: while all
 * admins share one login, this resolves to that shared address for everyone
 * — real per-admin accounts make the same field meaningful with no change
 * here.
 *
 * @returns {string}
 */
export const resolveAdminActor = () => auth?.currentUser?.email || PLACEHOLDER_ADMIN_ACTOR;

/**
 * Appends one entry to /auditLog. Always a push() — see the append-only note
 * above. Never throws: a failed audit write must not break the booking/Open
 * Play action it's attached to (e.g. rules not deployed yet, or a transient
 * network error) — it's logged to the console and swallowed instead.
 *
 * @param {object} entry
 * @param {string} entry.actionType - one of AUDIT_ACTION_TYPES
 * @param {string} entry.entityType - one of ENTITY_TYPES
 * @param {string} entry.entityId - the booking/session/join's Firebase id
 *   (for a grouped multi-date booking, its group/primary id — the same id
 *   the admin screens already treat as "the booking")
 * @param {string} [entry.performedBy] - defaults to PLACEHOLDER_ADMIN_ACTOR
 * @param {string|null} [entry.statusOld] - only for a real status transition
 * @param {string|null} [entry.statusNew]
 * @param {string|null} [entry.reason] - decline/cancel reason, when captured
 * @param {object|null} [entry.summary] - a small snapshot for display without
 *   a follow-up lookup: { guestName, court, date, timeSlots, sportName, type,
 *   confirmationId, ... } — shape is deliberately loose since booking and
 *   Open Play summaries carry different fields.
 * @param {object|null} [entry.changes] - optional before/after of whichever
 *   fields actually changed (court, timeslot, equipment, payment amount...),
 *   for an edit where statusOld/statusNew alone wouldn't show what happened.
 */
export const logAuditEvent = async ({
  actionType,
  entityType,
  entityId,
  // Omit for an admin action and the signed-in account's email is recorded;
  // guest-facing call sites pass the guest's own name instead.
  performedBy = null,
  statusOld = null,
  statusNew = null,
  reason = null,
  summary = null,
  changes = null,
}) => {
  if (!db) return;
  if (!actionType || !entityType || !entityId) {
    console.error('logAuditEvent: actionType, entityType and entityId are required.', { actionType, entityType, entityId });
    return;
  }
  try {
    await push(dbRef(db, 'auditLog'), {
      timestamp: Date.now(),
      actionType,
      entityType,
      entityId,
      performedBy: performedBy || resolveAdminActor(),
      // Firebase strips `undefined` (turning the key absent, which reads
      // back as `undefined` too) but keeps explicit `null` — using null here
      // means every entry has these keys, so the admin screen never has to
      // special-case a missing field vs. a genuinely empty one.
      statusOld: statusOld ?? null,
      statusNew: statusNew ?? null,
      reason: reason ?? null,
      summary: summary ?? null,
      changes: changes ?? null,
    });
  } catch (err) {
    console.error('Audit log write failed (booking/Open Play action itself still succeeded):', err);
  }
};

/**
 * Every audit entry for one booking/session/join, oldest first — "all
 * history for this booking". Uses the entityId index.
 * @param {string} entityId
 * @returns {Promise<Array<object>>}
 */
export const fetchAuditLogForEntity = async (entityId) => {
  if (!db || !entityId) return [];
  const q = query(dbRef(db, 'auditLog'), orderByChild('entityId'), equalTo(entityId));
  const snapshot = await get(q);
  const list = [];
  if (snapshot.exists()) {
    // The braces are load-bearing. Firebase's DataSnapshot.forEach is not
    // Array.forEach: returning a TRUTHY value from the callback CANCELS the
    // enumeration. `list.push()` returns the new array length, so a concise
    // arrow body (`child => list.push(...)`) returns 1 on the first child,
    // Firebase reads that as "stop", and the query silently yields exactly
    // one row no matter how many exist. Keep this a statement body.
    snapshot.forEach((child) => { list.push({ id: child.key, ...child.val() }); });
  }
  list.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  return list;
};

/**
 * Audit entries within a timestamp range (inclusive), newest first — what
 * the Audit Trail screen's date filter runs against. Uses the timestamp
 * index, same startAt/endAt range-query pattern AdminBooking.jsx's list view
 * already uses for /bookings.
 * @param {number} startMs
 * @param {number} endMs
 * @returns {Promise<Array<object>>}
 */
export const fetchAuditLogInRange = async (startMs, endMs) => {
  if (!db) return [];
  const q = query(dbRef(db, 'auditLog'), orderByChild('timestamp'), startAt(startMs), endAt(endMs));
  const snapshot = await get(q);
  const list = [];
  if (snapshot.exists()) {
    // The braces are load-bearing. Firebase's DataSnapshot.forEach is not
    // Array.forEach: returning a TRUTHY value from the callback CANCELS the
    // enumeration. `list.push()` returns the new array length, so a concise
    // arrow body (`child => list.push(...)`) returns 1 on the first child,
    // Firebase reads that as "stop", and the query silently yields exactly
    // one row no matter how many exist. Keep this a statement body.
    snapshot.forEach((child) => { list.push({ id: child.key, ...child.val() }); });
  }
  list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  return list;
};

/**
 * Turns a booking's date-groups into { [court]: [timeSlot, ...] } — the shape
 * the Audit Trail renders one line per court from.
 *
 * Shared rather than duplicated because three places need the identical
 * result: the guest submit path, the admin action paths, and the screen's
 * own fallback when an entry was written without it.
 *
 * @param {Array<{timeSlots?: Object}>} dateGroups
 * @returns {Object}
 */
export const buildSlotsByCourt = (dateGroups) => {
  const slotsByCourt = {};
  (dateGroups || []).forEach((group) => {
    Object.entries(group?.timeSlots || {}).forEach(([timeSlot, courts]) => {
      const courtList = Array.isArray(courts) ? courts : Object.keys(courts || {}).map(Number);
      courtList.forEach((court) => {
        if (!slotsByCourt[court]) slotsByCourt[court] = [];
        if (!slotsByCourt[court].includes(timeSlot)) slotsByCourt[court].push(timeSlot);
      });
    });
  });
  return slotsByCourt;
};

/**
 * The `summary` snapshot for a BOOKING audit entry.
 *
 * Accepts any of the booking shapes the app passes around — a raw date-record
 * (`originalData`), a grouped queue entry with a `dates` array, or the
 * combined object the guest checkout builds — because all three were being
 * summarised by near-identical inline code in three files, which is exactly
 * how the flat courts[]/timeSlots[] shape survived in one place after being
 * replaced in another.
 *
 * @param {object} data - a booking record in any of those shapes
 * @returns {object|null}
 */
/**
 * Flags an audit summary as an edit made on a PAST date, so the Audit Trail
 * can badge it — a change to a finished day rewrites history the club has
 * already reported on, and is worth being able to spot at a glance.
 *
 * @param {object|null} summary - the entry's summary
 * @param {string[]} dateStrs - 'yyyy-MM-dd' dates the edit touched
 * @returns {object|null} the summary, with `pastDateEdit: true` when any date
 *   is before today (Philippine time); unchanged otherwise
 */
export const markPastDateEdit = (summary, dateStrs) => {
  const today = phTodayStr();
  const isPast = (dateStrs || []).some(d => typeof d === 'string' && d < today);
  if (!isPast) return summary;
  return { ...(summary || {}), pastDateEdit: true };
};

export const buildBookingAuditSummary = (data) => {
  if (!data) return null;
  const dateGroups = (Array.isArray(data.dates) && data.dates.length > 0)
    ? data.dates
    : [{ date: data.date, timeSlots: data.timeSlots }];
  return {
    guestName: data.userName || null,
    confirmationId: data.confirmationId || null,
    date: dateGroups[0]?.date || null,
    dates: dateGroups.map(g => g.date).filter(Boolean),
    sportId: data.sportId || null,
    slotsByCourt: buildSlotsByCourt(dateGroups),
    type: (data.bookingSource === 'package' || data.packageId) ? 'package' : 'booking',
    packageName: data.packageName || null,
    // The guest's own "Partial payment" choice at checkout, if they made one.
    guestPartialPercent: data.guestPartialPayment ? (data.guestPartialPercent ?? null) : null,
  };
};

/**
 * The individual player names on an Open Play join.
 *
 * The guest join form collects everyone by name (`players`), while an
 * admin-entered walk-in only has the booker's name and a headcount — so this
 * returns the roster where one exists and an empty list otherwise, rather
 * than inventing "Guest 2", "Guest 3" placeholders for people whose names
 * were never captured.
 *
 * @param {object} rec - an openPlayJoins record
 * @returns {string[]}
 */
export const auditPlayerNames = (rec) => (Array.isArray(rec?.players) ? rec.players : [])
  .map(p => String(p?.name || '').trim())
  .filter(Boolean);

/**
 * The `summary` snapshot for an OPEN PLAY JOIN audit entry — was written out
 * by hand at four separate call sites in openPlayUtil.js, which is four
 * places to forget a field.
 *
 * @param {object} rec - an openPlayJoins record
 * @returns {object|null}
 */
export const buildOpenPlayJoinSummary = (rec) => {
  if (!rec) return null;
  return {
    guestName: rec.fullName || null,
    confirmationId: rec.confirmationId || null,
    date: rec.date || null,
    sportName: rec.sportName || null,
    sessionId: rec.sessionId || null,
    numPlayers: rec.numPlayers || null,
    // Set only when the guest joined through a specific grid cell, so the
    // Audit Trail can show which hour was joined rather than just the day.
    timeSlot: rec.timeSlot || null,
    players: auditPlayerNames(rec),
    type: 'openplay',
  };
};

/**
 * A promo, snapshotted for the trail (Admin -> Promo).
 *
 * Everything the row needs travels in the entry itself. A promo lives in
 * `config.promos` and is edited in place, so re-reading it later would show
 * today's rates against last week's action — and a deleted one would read as
 * nothing at all, which is exactly the entry an admin most needs.
 *
 * `rateCount` rather than the rules themselves: the trail says a promo
 * changed and what it covered, not the full pricing table, and the rules are
 * where most of a promo's bulk lives.
 *
 * @param {object} promo
 * @param {string|null} [sportName] - resolved by the caller, which has config
 * @returns {object|null}
 */
export const buildPromoSummary = (promo, sportName = null) => {
  if (!promo) return null;
  return {
    promoName: promo.name || null,
    startDate: promo.startDate || null,
    endDate: promo.endDate || null,
    sportName: sportName || null,
    rateCount: Array.isArray(promo.rules) ? promo.rules.length : 0,
    type: 'promo',
  };
};

/**
 * Reads the booking records behind a set of audit entries, by key.
 *
 * WHY THE SCREEN NEEDS THIS AT ALL
 * --------------------------------
 * An audit entry carries a `summary` snapshot so the trail can be read
 * without touching the bookings it describes. But that snapshot is only as
 * good as the code that wrote it: entries written before a field existed, or
 * by a browser tab still running an older bundle, arrive missing pieces —
 * and the screen then renders a row that says almost nothing, which is the
 * one thing an audit trail must not do.
 *
 * So the summary stays the preferred source, and this fills the gaps from
 * the booking itself. The trade-off is deliberate and worth stating: a
 * back-filled field reflects the booking as it is NOW, not as it was at the
 * time of the action. Snapshot values are always used when present, so this
 * only ever applies where the alternative was showing nothing.
 *
 * Missing/denied records resolve to null and are cached as such, so a bad id
 * is not retried on every render.
 *
 * @param {string[]} bookingIds
 * @returns {Promise<Object<string, object|null>>}
 */
export const fetchBookingsByIds = async (bookingIds) => {
  const out = {};
  if (!db || !Array.isArray(bookingIds) || bookingIds.length === 0) return out;
  await Promise.all(bookingIds.map(async (id) => {
    if (!id) return;
    try {
      const snap = await get(dbRef(db, `bookings/${id}`));
      out[id] = snap.exists() ? snap.val() : null;
    } catch (err) {
      console.error('Audit trail: could not read booking', id, err);
      out[id] = null;
    }
  }));
  return out;
};

/**
 * The Open Play sessions behind a set of audit entries, by key — the
 * counterpart of fetchBookingsByIds, and there for the same reason.
 *
 * A session entry's own id IS the session id; a JOIN entry carries the
 * session it belongs to in `summary.sessionId`. Either way the session
 * record is what holds `courts` ({ [court]: [timeSlot, ...] }), so this is
 * how a join row gets court and hour lines at all — a join stores no courts
 * of its own, only which session it joined.
 *
 * @param {string[]} sessionIds
 * @returns {Promise<Object<string, object|null>>}
 */
export const fetchOpenPlaySessionsByIds = async (sessionIds) => {
  const out = {};
  if (!db || !Array.isArray(sessionIds) || sessionIds.length === 0) return out;
  await Promise.all(sessionIds.map(async (id) => {
    if (!id) return;
    try {
      const snap = await get(dbRef(db, `openPlaySessions/${id}`));
      out[id] = snap.exists() ? snap.val() : null;
    } catch (err) {
      console.error('Audit trail: could not read Open Play session', id, err);
      out[id] = null;
    }
  }));
  return out;
};

/**
 * Live-subscribes to every audit entry inside a timestamp range, newest
 * first. This is the screen's ONLY read path: the first load asks for the
 * last DEFAULT_AUDIT_DAYS days, and picking a different date range simply
 * re-subscribes with new bounds, so nothing is ever fetched that the admin
 * did not ask to see.
 *
 * WHY A RANGED LISTENER RATHER THAN limitToLast + A SEPARATE get()
 * ----------------------------------------------------------------
 * The earlier shape kept a live "most recent N" listener AND a one-shot
 * ranged get(), and rendered whichever the filter selected. That had two
 * failure modes, both of which cost real debugging time: the get() never
 * re-ran when a new entry was logged (so an action taken after the range was
 * applied stayed invisible), and the unbounded 500-entry listener defeated
 * the point of having a date filter at all. One live ranged query is both
 * bounded and self-updating, so a confirm/decline lands on screen the moment
 * it is written, without a refetch.
 *
 * `onError` exists because a failed listener and a genuinely empty log are
 * otherwise indistinguishable to the caller — both arrive as an empty array,
 * and the screen renders "no activity" for what is really a denied read or a
 * missing index. An audit trail that quietly shows nothing is worse than one
 * that shows an error: "nothing happened" is itself a claim about the record.
 *
 * Requires .indexOn ["timestamp"] on /auditLog.
 *
 * @param {number} startMs - inclusive lower bound
 * @param {number} endMs - inclusive upper bound
 * @param {(entries: object[]) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeAuditLogInRange = (startMs, endMs, callback, onError = null) => {
  if (!db) {
    if (onError) onError(new Error('Database not configured.'));
    callback([]);
    return () => {};
  }
  const q = query(dbRef(db, 'auditLog'), orderByChild('timestamp'), startAt(startMs), endAt(endMs));
  const unsubscribe = onValue(q, (snapshot) => {
    const list = [];
    // The braces are load-bearing. Firebase's DataSnapshot.forEach is not
    // Array.forEach: returning a TRUTHY value from the callback CANCELS the
    // enumeration. `list.push()` returns the new array length, so a concise
    // arrow body (`child => list.push(...)`) returns 1 on the first child,
    // Firebase reads that as "stop", and the query silently yields exactly
    // one row no matter how many exist. Keep this a statement body.
    snapshot.forEach((child) => { list.push({ id: child.key, ...child.val() }); });
    list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    callback(list);
  }, (err) => {
    console.error('Audit log listener failed:', err);
    if (onError) onError(err);
    callback([]);
  });
  return unsubscribe;
};
